<template>
  <div :class="$style.PWFieldWrapper">
    <div
      :class="[
        $style.PWTextarea,
        isFocused && $style.PWTextareaFocus,
        isError && $style.PWTextareaError,
      ]"
      :aria-disabled="disabled"
    >
      <label
        :for="id"
        :class="[
          $style.PWTextareaPlaceholder,
          placeholderToUp && $style.PWTextareaPlaceholderToUp,
        ]"
      >
        {{ placeholder }}
      </label>
      <textarea
        :id="id"
        ref="ta"
        :value="value"
        :disabled="disabled"
        :required="required"
        :class="$style.PWTextareaArea"
        :rows="lines"
        :style="{
          width
        }"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
      />
    </div>
    <div
      v-if="$slots.default"
      :class="[
        $style.PWFieldTip,
        isError && $style.PWFieldTipError,
      ]"
    >
      <slot />
    </div>
  </div>
</template>

<script>
const calculateContentHeight = (ta, scanAmount) => {
  const origHeight = ta.style.height
  const scrollHeight = ta.scrollHeight
  const overflow = ta.style.overflow
  let height = ta.offsetHeight

  /// only bother if the ta is bigger than content
  if (height >= scrollHeight) {
    /// check that our browser supports changing dimension
    /// calculations mid-way through a function call...
    ta.style.height = (height + scanAmount) + 'px'
    /// because the scrollbar can cause calculation problems
    ta.style.overflow = 'hidden'
    /// by checking that scrollHeight has updated
    if (scrollHeight < ta.scrollHeight) {
      /// now try and scan the ta's height downwards
      /// until scrollHeight becomes larger than height
      while (ta.offsetHeight >= ta.scrollHeight) {
        ta.style.height = (height -= scanAmount) + 'px'
      }
      /// be more specific to get the exact height
      while (ta.offsetHeight < ta.scrollHeight) {
        ta.style.height = (height++) + 'px'
      }
      /// reset the ta back to it's original height
      ta.style.height = origHeight
      /// put the overflow back
      ta.style.overflow = overflow
      return height
    }
  } else {
    return scrollHeight
  }
}

export default {
  props: {
    id: {
      type: String,
      default: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    },
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: undefined
    },
    required: {
      type: Boolean,
      default: undefined
    },
    isError: {
      type: Boolean,
      default: undefined
    },
    minRows: {
      type: Number,
      default: 2
    },
    width: {
      type: String,
      default: '100%'
    }
  },
  data () {
    return {
      isFocused: false,
      lines: this.minRows
    }
  },
  computed: {
    valueIsNotEmpty () {
      return typeof this.value === 'string' && this.value.length > 0
    },
    placeholderToUp () {
      return this.valueIsNotEmpty || this.isFocused
    }
  },
  mounted () {
    this.calculateHeight()
  },
  methods: {
    handleFocus () {
      this.isFocused = true
    },
    handleBlur () {
      this.isFocused = false
    },
    handleInput (ev) {
      this.calculateHeight()
      const { target } = ev
      this.$emit('input', target.value)
    },
    calculateHeight () {
      const ta = this.$refs.ta
      const style = (window.getComputedStyle)
        ? window.getComputedStyle(ta)
        : ta.currentStyle
      // This will get the line-height only if it is set in the css,
      // otherwise it's "normal"
      const taLineHeight = parseInt(style.lineHeight, 10)
      // Get the scroll height of the textarea
      const taHeight = calculateContentHeight(ta, taLineHeight)
      // calculate the number of lines
      const numberOfLines = Math.ceil(taHeight / taLineHeight)
      this.lines = this.minRows > numberOfLines
        ? this.minRows
        : numberOfLines - 1
    }
  }
}
</script>

<style module lang="scss">
@use '@/assets/scss/mixins';

.PWFieldWrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.PWFieldTip {
  padding: 0 14px;
  color: var(--secondary);

  @include mixins.caption;

  &.PWFieldTipError {
    color: var(--main-red);
  }
}

.PWTextarea {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  width: 100%;
  border-radius: 4px;
  transition: box-shadow 0.3s;
}

.PWTextarea:not([aria-disabled]) {
  background-color: var(--white);
  box-shadow: inset 0 0 0 1px var(--grey-200);

  &:hover:not(.PWTextareaFocus):not(.PWTextareaError) {
    box-shadow: inset 0 0 0 1px var(--grey-900);
  }

  &.PWTextareaFocus {
    box-shadow: inset 0 0 0 2px var(--main-blue);
  }

  &.PWTextareaError {
    box-shadow: inset 0 0 0 1px var(--main-red);

    .PWTextareaPlaceholder {
      color: var(--main-red);
    }
  }
}

.PWTextarea[aria-disabled] {
  box-shadow: inset 0 0 0 1px var(--grey-200);
  background-color: var(--grey-100);

  .PWTextareaArea,
  .PWTextareaPlaceholder {
    color: var(--disabled);
  }
}

.PWTextareaArea {
  position: relative;
  border: none;
  z-index: 2;
  box-sizing: border-box;
  display: inline-flex;
  width: 100%;
  padding: 24px 12px 8px 12px;
  color: var(--primary);
  background-color: transparent;

  @include mixins.body1;
}

.PWTextareaPlaceholder {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 12px;
  display: -webkit-box !important;
  max-width: calc(100% - 32px);
  -webkit-line-clamp: 1 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1 !important;
  transform: translateY(20px) scale(1);
  transform-origin: top left;
  transition: transform 0.3s;
  color: var(--secondary);

  @include mixins.body1;
}

.PWTextareaFocus {
  box-shadow: inset 0 0 0 1px currentColor;
}

.PWTextareaPlaceholderToUp {
  transform: translateY(9px) scale(0.75);
}
</style>
